import useTranslate from 'modules/local/useTranslate'
import React, {useMemo, useState} from 'react'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import {announcement_create_Api, announcement_delete_Api, announcement_update_Api} from 'apis'
import {NullAsDefault} from 'views/Shared'
import {NotificationActionTypes, successNotify} from 'views/AppLocals/factory/createNotification'
import {showDeleteConfirmDialog} from 'views/AppLocals/factory/createConfirmDialog'
import {createModalTitle} from 'components/renderFunctions/createModalTitle'
import {getId} from 'apis/model/base'
import {Modal} from 'antd'
import CRUD from 'views/Shared/enums/CRUD'
import {AnnouncementCreateForm, AnnouncementUpdateForm} from './AnnouncementForm'

export const useAnnouncementForm = ({
                                        owner_type,
                                        organization,
                                        groups,
                                        defaultGroup,
                                        group,
                                        course_class,
                                        notifyListeners = {
                                            onCreated: undefined,
                                            onUpdated: undefined,
                                            onDeleted: undefined,
                                        },
                                    }) => {
    const t = useTranslate()
    const isCourseClass = owner_type === 'course_class'
    // owner_type='group': announcement của 1 group độc lập (không thuộc org, vd choir) - context group
    // đã cố định, không cần picker chọn group/ministry con như owner_type='organization'.
    const isGroup = owner_type === 'group'

    const [refresh_token, set_refresh_token] = useState()

    const [action, setAction] = useState({
        type: undefined,
        value: undefined,
    })

    const {
        onCreated = NullAsDefault,
        onUpdated = NullAsDefault,
        onDeleted = NullAsDefault,
    } = notifyListeners ?? {}

    const onCancel = () => {
        setAction({})
    }

    const onRefresh = () => {
        onCancel()
        set_refresh_token(Date.now())
    }

    const onError = notifyOnError(t)

    const {handleAsyncAction: onCreate, ...creatingAsync} = useAsyncAction({
        onError,
        apiInfo: announcement_create_Api,
        onSuccess: (result, data) => {
            successNotify(NotificationActionTypes.CREATE, t)
            onCreated(result, data)
            onRefresh()
        },
    })

    const {handleAsyncAction: onUpdate, ...updatingAsync} = useAsyncAction({
        onError,
        apiInfo: announcement_update_Api,
        onSuccess: (result, data) => {
            successNotify(NotificationActionTypes.UPDATE, t)
            onUpdated(result, data)
            onCancel()
        },
    })

    const {handleAsyncAction: onDelete} = useAsyncAction({
        onError,
        apiInfo: announcement_delete_Api,
        onSuccess: (result, data) => {
            successNotify(NotificationActionTypes.DELETE, t)
            onDeleted(result, data)
            onRefresh()
        },
    })

    const handleAction = (action, params) => {
        switch (action) {
            case CRUD.CREATE:
            case CRUD.UPDATE:
                setAction({
                    type: action,
                    value: params,
                })
                break
            case CRUD.DELETE:
                showDeleteConfirmDialog({
                    translate: t,
                    title: 'delete this item',
                    message: 'are you sure you want to delete this',
                    onOk: () => {
                        onDelete({}, {id: params?.id})
                    },
                })
            default:
                break
        }
    }

    const modal = useMemo(() => {
        let params = {}
        let modal_params = {}

        switch (action?.type) {
            case CRUD.CREATE:
                modal_params = {
                    title: createModalTitle('create an announcement'),
                    width: 600,
                }
                params = {
                    initialValues: {
                        ...(action?.value || {}),
                        ...(isCourseClass
                            ? {}
                            : isGroup
                                ? {group}
                                : {
                                    organization,
                                    ...(defaultGroup?.id ? {group_id: defaultGroup?.id} : {})
                                }),
                    },
                    isSubmitting: creatingAsync.isLoading,
                    onSubmit: (values) => {
                        onCreate(
                            isCourseClass
                                ? {...values, owner_type: 'course_class', course_class_id: getId(course_class)}
                                : isGroup
                                    ? {...values, owner_type: 'group', group_id: getId(group)}
                                    : {...values, owner_type: 'organization', organization_id: getId(organization)},
                            {}
                        )
                    },
                }
                break
            case CRUD.UPDATE:
                const announcement_id = getId(action?.value)
                modal_params = {
                    title: createModalTitle('edit announcement'),
                    width: 600,
                }
                params = {
                    initialValues: {...action?.value},
                    isSubmitting: updatingAsync.isLoading,
                    onSubmit: (values) => {
                        onUpdate(values, {id: announcement_id})
                    },
                }
                break
            default:
                return null
        }

        const Component = action?.type === CRUD.UPDATE ? AnnouncementUpdateForm : AnnouncementCreateForm

        return (
            <Modal
                footer={null}
                onCancel={onCancel}
                open={action?.type}
                destroyOnHidden={true}
                className="custom-modal"
                {...modal_params}>
                <Component
                    {...{onCancel}}
                    {...params}
                    owner_type={owner_type}
                    organization={organization}
                    groups={groups}
                    group={group}
                    course_class={course_class}
                />
            </Modal>
        )
    }, [action, owner_type, organization, group, course_class, creatingAsync.isLoading, updatingAsync.isLoading])

    return {
        modal,
        action,
        handleAction,
        refresh_token,
        onRefresh,
    }
}
